/**
 * MODULE: domain/pages/frontend/components/editor/PagesCalloutBlockView.tsx
 * PURPOSE: Highlighted callout block in the Pages note editor
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { KeyboardEvent } from 'react';
import {
  CALLOUT_VARIANTS,
  calloutVariantClasses,
  calloutVariantLabel,
  normalizeCalloutVariant,
} from '../../utils/pagesCalloutUtils';

export function PagesCalloutBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const editable = editor.isEditable;
  const text = String(node.attrs.text ?? '');
  const variant = normalizeCalloutVariant(node.attrs.variant);

  const stopKeys = (event: KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <NodeViewWrapper
      className={`my-3 ${selected ? 'ring-2 ring-blue-300 ring-offset-2 dark:ring-blue-700 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-callout-block"
      data-variant={variant}
    >
      <div className={`rounded-lg border-l-4 px-4 py-3 ${calloutVariantClasses(variant)}`}>
        {editable ? (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="pages-callout-variant-picker">
            {CALLOUT_VARIANTS.map((option) => {
              const active = option === variant;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={active}
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                    active
                      ? 'bg-white/80 text-gray-900 shadow-sm dark:bg-gray-950/80 dark:text-white'
                      : 'bg-black/5 text-current hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/15'
                  }`}
                  onClick={() => updateAttributes({ variant: option })}
                  data-testid={`pages-callout-variant-${option}`}
                >
                  {calloutVariantLabel(option)}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-80">
            {calloutVariantLabel(variant)}
          </p>
        )}
        {editable ? (
          <textarea
            value={text}
            onChange={(event) => updateAttributes({ text: event.target.value })}
            onKeyDown={stopKeys}
            placeholder="Callout text…"
            rows={3}
            className="w-full resize-y border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
            data-testid="pages-callout-text-input"
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm" data-testid="pages-callout-text">
            {text}
          </p>
        )}
      </div>
    </NodeViewWrapper>
  );
}
